import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { Button } from '../ui/button';
import { 
  ArrowRight, 
  Users, 
  FileCheck, 
  Award, 
  Shield, 
  BarChart3, 
  Bell, 
  Calendar,
  Building2,
  CheckCircle,
  Sparkles,
  ExternalLink,
  Clock,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';

const apmFeatures = [
  {
    icon: Users,
    title: "Gestión de Mayoristas y Vendors",
    description: "Centraliza toda la información de tus proveedores, mayoristas y software vendors en un solo lugar. Contactos, acuerdos comerciales y niveles de partnership.",
    highlights: ["Directorio centralizado", "Niveles de partnership", "Historial de relación"]
  },
  {
    icon: FileCheck,
    title: "Seguimiento de Contratos",
    description: "Monitorea fechas de vencimiento, renovaciones automáticas y cumplimiento de SLAs. Nunca más pierdas una renovación importante.",
    highlights: ["Alertas de vencimiento", "Tracking de SLAs", "Documentos centralizados"]
  },
  {
    icon: Award,
    title: "Gestión de Certificaciones",
    description: "Controla las certificaciones de tu equipo técnico y comercial. Fechas de expiración, requisitos de renovación y planes de capacitación.",
    highlights: ["Tracking por persona", "Alertas de expiración", "Requisitos de vendor"]
  },
  {
    icon: TrendingUp,
    title: "Análisis de Performance",
    description: "Visualiza métricas clave de cada relación comercial. Revenue por vendor, cumplimiento de objetivos y oportunidades de crecimiento.",
    highlights: ["Dashboard analítico", "KPIs por partner", "Proyecciones"]
  }
];

const benefits = [
  { icon: Clock, text: "Ahorra +10 horas/semana en gestión administrativa" },
  { icon: AlertTriangle, text: "Reduce el riesgo de perder certificaciones críticas" },
  { icon: Shield, text: "Cumplimiento garantizado de compromisos contractuales" },
  { icon: BarChart3, text: "Visibilidad total del ecosistema de partners" }
];

const APMPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="APM Beta · Automation & Partner Management"
        description="Accede al beta de APM (Automation & Partner Management): automatiza procesos con proveedores, certificaciones y contratos. Prueba limitada con acceso prioritario."
        keywords="APM, Automation Partner Management, gestión proveedores, asisant beta"
        canonical="/apm"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-dark-ellipse">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Beta Badge */}
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm rounded-full px-5 py-2 mb-8 border border-yellow-400/40">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-yellow-300 text-sm font-bold uppercase tracking-wider">Beta Disponible</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Asisant <span className="text-yellow-300">Partner Manager</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed">
              La plataforma integral para gestionar tus relaciones con proveedores, mayoristas y software vendors. 
              Contratos, certificaciones y cumplimiento en un solo lugar.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://partnermanager.asisant.com" target="_blank" rel="noopener noreferrer">
                <Button
                  size="lg"
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group"
                >
                  Acceder a la Beta
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <Link to="/contacto?source=apm">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 group bg-transparent"
                >
                  Solicitar demo
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              Características
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Todo lo que necesitas para{' '}
              <span className="text-[#1297fd]">gestionar partners</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              APM centraliza la gestión de tus relaciones comerciales más importantes en una sola plataforma.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {apmFeatures.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-3xl p-8 border border-gray-100 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{feature.description}</p>
                <div className="flex flex-wrap gap-2">
                  {feature.highlights.map((highlight, hIdx) => (
                    <span 
                      key={hIdx}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm"
                    >
                      <CheckCircle className="w-3 h-3" />
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-[#e3e4e6]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
                Beneficios
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Optimiza tu ecosistema de{' '}
                <span className="text-[#1297fd]">partners</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Con APM, tu equipo dedica menos tiempo a tareas administrativas y más tiempo a construir relaciones comerciales estratégicas.
              </p>
              
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#8bcaf9]/20">
                      <benefit.icon className="w-5 h-5 text-[#1297fd]" />
                    </div>
                    <span className="text-gray-700 font-medium">{benefit.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              {/* Dashboard Preview Card */}
              <div className="bg-white rounded-3xl p-1 shadow-2xl border border-gray-100">
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-yellow-400">
                        <span className="text-gray-900 font-bold text-sm">APM</span>
                      </div>
                      <span className="text-white font-semibold">Partner Manager</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-[#8bcaf9] text-xs mb-1">Partners</p>
                      <p className="text-white font-bold text-xl">24</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-yellow-400 text-xs mb-1">Certificaciones</p>
                      <p className="text-white font-bold text-xl">47</p>
                    </div>
                    <div className="bg-white/5 rounded-xl p-4">
                      <p className="text-green-400 text-xs mb-1">Contratos</p>
                      <p className="text-white font-bold text-xl">18</p>
                    </div>
                  </div>

                  {/* Alerts */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-yellow-400/10 rounded-lg p-3 border border-yellow-400/20">
                      <Bell className="w-4 h-4 text-yellow-400" />
                      <span className="text-yellow-200 text-sm">3 certificaciones por vencer este mes</span>
                    </div>
                    <div className="flex items-center gap-3 bg-[#1297fd]/10 rounded-lg p-3 border border-[#1297fd]/20">
                      <Calendar className="w-4 h-4 text-[#40aaf8]" />
                      <span className="text-[#8bcaf9] text-sm">2 contratos requieren renovación</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -top-4 -right-4 bg-yellow-400 rounded-2xl p-4 shadow-xl">
                <p className="text-gray-900 font-bold text-lg">Beta</p>
                <p className="text-gray-700 text-xs">Acceso anticipado</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-[#8bcaf9]/20 text-[#1297fd] rounded-full text-sm font-medium mb-4">
              Casos de Uso
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ideal para equipos que gestionan{' '}
              <span className="text-[#1297fd]">múltiples partners</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#8bcaf9]/10 to-transparent border border-[#8bcaf9]/20">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Distribuidores IT</h3>
              <p className="text-gray-600">Gestiona relaciones con múltiples mayoristas y vendors de software simultáneamente.</p>
            </div>

            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#8bcaf9]/10 to-transparent border border-[#8bcaf9]/20">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">MSPs</h3>
              <p className="text-gray-600">Controla certificaciones técnicas y cumplimiento de SLAs con tus proveedores de servicios.</p>
            </div>

            <div className="text-center p-8 rounded-3xl bg-gradient-to-br from-[#8bcaf9]/10 to-transparent border border-[#8bcaf9]/20">
              <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Integradores</h3>
              <p className="text-gray-600">Coordina equipos técnicos y sus certificaciones con los requisitos de cada vendor.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-dark-ellipse">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm rounded-full px-5 py-2 mb-8 border border-yellow-400/40">
            <Sparkles className="w-4 h-4 text-yellow-300" />
            <span className="text-yellow-300 text-sm font-bold">Acceso Beta Gratuito</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Sé de los primeros en probar APM
          </h2>
          <p className="text-lg text-white/80 mb-10">
            Únete a la beta y ayúdanos a construir la mejor herramienta de gestión de partners del mercado.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://partnermanager.asisant.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-semibold px-10 py-6 rounded-full transition-all hover:scale-105 group">
                Acceder ahora
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <Link to="/contacto?source=apm">
              <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-10 py-6 rounded-full transition-all hover:scale-105 bg-transparent">
                Contactar equipo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default APMPage;
